/**
 * Standardized delete-confirmation dialog — the one title/body/button shape
 * every "delete this row" affordance in the app presents, so a user sees the
 * same confirmation regardless of which grid, card, or form triggered it.
 * Presentational only: the calling surface supplies what is being deleted
 * and owns the actual mutation through useDeleteConfirm().
 *
 * Contains:
 * - DeleteConfirmDialog — danger-styled ConfirmationDialog preset
 *
 * Pairs with: useDeleteConfirm (src/hooks/useDeleteConfirm.js)
 */

import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

import ConfirmationDialog from '@sophocles/components/ConfirmationDialog/ConfirmationDialog';

export default function DeleteConfirmDialog({ open, entityLabel, body, submitting, onConfirm, onCancel }) {
  const { t } = useTranslation();

  return (
    <ConfirmationDialog
      open={open}
      title={t('common.deleteConfirmTitle', 'Delete {{entity}}?', { entity: entityLabel })}
      submit={onConfirm}
      closeDialog={onCancel}
      submitLabel={t('actions.delete', 'Delete')}
      cancelLabel={t('actions.cancel', 'Cancel')}
      submitColor="error"
      submitDisabled={submitting}
    >
      {body ??
        t('common.deleteConfirmBody', 'This will permanently delete this {{entity}}. This action cannot be undone.', {
          entity: entityLabel,
        })}
    </ConfirmationDialog>
  );
}

DeleteConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  entityLabel: PropTypes.string.isRequired,
  // Overrides the default "permanently delete, cannot be undone" body — for
  // callers whose action is actually reversible (e.g. deactivate) or carries
  // its own caveat, so the copy doesn't misstate what's about to happen.
  body: PropTypes.node,
  submitting: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

DeleteConfirmDialog.defaultProps = {
  body: null,
  submitting: false,
};
